import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Drawer } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { parseDateOnly } from 'services/DateService';
import { Colors } from 'context';
import { CareerIcon, Icon, IconNames } from 'atoms';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { formatSalaryRange, transformSkillMatch, useCareerVacancy } from 'core';
import { CareerCloseButton } from 'molecules/CareerCloseButton/CareerCloseButton';
import { VacancySkillMatch } from 'molecules/VacancySkillMatch/VacancySkillMatch';
import { careerColor, careerTokenVars, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

/** Строка ленты, по которой панель открывается сразу, пока грузятся детали. */
export interface IVacancyPreview {
  id: string;
  title: string;
  companyName: string;
  location: string;
  applyUrl: string;
}

export interface ICareerVacancyDetails {
  /** `null` держит панель закрытой. Панель сама грузит вакансию по id —
   *  именно поэтому её больше не закрывает фоновой перезапрос ленты. */
  vacancyId: string | null;
  onClose: () => void;
  onSave: () => void;
  /**
   * Можно ли сохранять. `false` — предпросмотр методиста (ТЗ 0039 доводка).
   * 🔴 Проп нужен и ЗДЕСЬ, не только на карточке: закладку в карточке спрятали,
   * а в подробном виде она осталась — то есть кнопку просто перенесли туда,
   * где её не искали.
   */
  canSave?: boolean;
  /**
   * Уход на сайт работодателя — см. комментарий у карточки вакансии.
   * 0073: строка передаётся из самой панели. Раньше её искали среди
   * загруженных строк ленты, и по прямой ссылке `?offer=` намерение терялось.
   */
  onApplyClick?: (row: IVacancyPreview) => void;
  /** Строка ленты: панель открывается сразу, не дожидаясь своего запроса. */
  preview?: IVacancyPreview | null;
  skillHref?: (slug: string) => string;
  actionLoading: boolean;
}

/**
 * Подробный вид вакансии.
 *
 * Зачем понадобился: в карточке описание обрезано тремя строками
 * (`-webkit-line-clamp`), а прочитать его целиком было НЕГДЕ — отдельной
 * страницы вакансии в LMS нет. Владелец на ручной проверке так и сказал:
 * непонятно, что это за вакансия и что с ней делать.
 *
 * Данные берёт СВОИМ запросом по id (ТЗ 0035), а не из загруженного списка.
 * Раньше он держался за `vacancies.find(...)` на запросе с политикой
 * `cache-and-network`, и фоновой перезапрос или смена фильтра закрывали дровер
 * на середине чтения. Плюс под пагинацией нужной строки в загруженной странице
 * может не оказаться вовсе, а полное описание теперь живёт только здесь —
 * список возит укороченное.
 */
export const CareerVacancyDetails = memo(
  ({
    vacancyId,
    onClose,
    onSave,
    onApplyClick,
    actionLoading,
    canSave = true,
    preview = null,
    skillHref,
  }: ICareerVacancyDetails) => {
    const { t } = useTranslation();
    const { vacancy, loading } = useCareerVacancy(vacancyId);

    const head: IVacancyPreview | null = vacancy
      ? {
          id: vacancy.id,
          title: vacancy.title,
          companyName: vacancy.companyName,
          location: vacancy.location,
          applyUrl: vacancy.applyUrl,
        }
      : preview && preview.id === vacancyId
      ? preview
      : null;

    if (!vacancyId || (!head && !loading)) {
      return null;
    }

    // Только 'saved' — тот же инвариант, что в CareerVacancyCard: дровер и
    // карточка показывают одну вакансию и обязаны сходиться.
    const isSaved = vacancy?.myInteraction === 'saved';

    const closeButton = <CareerCloseButton onClick={onClose} />;

    if (!vacancy) {
      // 0073: панель открыта сразу. Пока детали грузятся — нейтральный блок
      // соответствия, без числа и без зелёных чипов.
      return (
        <Drawer
          open
          onClose={onClose}
          width="min(560px, 100vw)"
          title={head?.title ?? ''}
          closable={false}
          extra={closeButton}
          destroyOnClose>
          <Body>
            {head ? (
              <CompanyLine>
                {head.companyName}
                {head.location ? ` · ${head.location}` : ''}
              </CompanyLine>
            ) : null}
            <VacancySkillMatch match={undefined} loading />
          </Body>
        </Drawer>
      );
    }

    const match = transformSkillMatch(vacancy.skillMatch, vacancy.skills ?? []);
    const owned = new Set(match?.matchedIds ?? []);

    const skills = vacancy.skills ?? [];
    const salary = formatSalaryRange(
      vacancy.salaryMin ?? null,
      vacancy.salaryMax ?? null,
      vacancy.salaryCurrency ?? '',
    );

    const facts: { key: string; icon: IconNames; label: string; value: string }[] = [
      {
        key: 'location',
        icon: IconNames.VacancyLocation,
        label: t('careerCenter.vacancies.facts.location'),
        value: vacancy.location,
      },
      {
        key: 'workMode',
        icon: IconNames.VacancyMode,
        label: t('careerCenter.vacancies.facts.workMode'),
        value: vacancy.workMode
          ? t(`careerProfile.workMode.${vacancy.workMode}`, { defaultValue: vacancy.workMode })
          : '',
      },
      {
        key: 'seniority',
        icon: IconNames.VacancyLevel,
        label: t('careerCenter.vacancies.facts.seniority'),
        value: vacancy.seniority
          ? t(`careerProfile.seniority.${vacancy.seniority}`, { defaultValue: vacancy.seniority })
          : '',
      },
      {
        key: 'employment',
        icon: IconNames.VacancyEmployment,
        label: t('careerCenter.vacancies.facts.employment'),
        value: vacancy.employmentType
          ? t(`careerCenter.vacancies.employment.${vacancy.employmentType}`, {
              defaultValue: vacancy.employmentType,
            })
          : '',
      },
      {
        key: 'salary',
        icon: IconNames.VacancySalary,
        label: t('careerCenter.vacancies.facts.salary'),
        value: salary,
      },
      {
        key: 'expires',
        icon: IconNames.Calendar,
        label: t('careerCenter.vacancies.expiresAt'),
        value: vacancy.expiresAt ? parseDateOnly(vacancy.expiresAt) : '',
      },
    ].filter((fact) => Boolean(fact.value));

    return (
      <Drawer
        open
        onClose={onClose}
        width="min(560px, 100vw)"
        title={vacancy.title}
        closable={false}
        extra={closeButton}
        destroyOnClose>
        <Body>
          <CompanyLine>
            {vacancy.companyName}
            {vacancy.location ? ` · ${vacancy.location}` : ''}
          </CompanyLine>

          {facts.length ? (
            <FactList>
              {facts.map((fact) => (
                <FactRow key={fact.key}>
                  <FactIcon>
                    <Icon name={fact.icon} width="16px" height="16px" />
                  </FactIcon>
                  <FactLabel>{fact.label}</FactLabel>
                  <FactValue>{fact.value}</FactValue>
                </FactRow>
              ))}
            </FactList>
          ) : null}

          {vacancy.description ? (
            <Section>
              <SectionTitle>{t('careerCenter.vacancies.facts.description')}</SectionTitle>
              {/* Описание — свободный текст из ERP; переносы строк сохраняем. */}
              <DescriptionText>{vacancy.description}</DescriptionText>
            </Section>
          ) : null}

          {(vacancy.directionTitles ?? []).length ? (
            <Section>
              <SectionTitle>{t('careerCenter.vacancies.facts.directions')}</SectionTitle>
              <TagRow>
                {(vacancy.directionTitles ?? []).map((title) => (
                  <Tag key={title}>{title}</Tag>
                ))}
              </TagRow>
            </Section>
          ) : null}

          {/* В карточке навыки обрезаны восемью — здесь показываем все.
            🔴 Рисуем из объектного списка `skills`, а не из плоского
            `skillNames`: знак технологии есть у карточки в ленте, и один и тот
            же навык не имеет права выглядеть на соседних экранах двумя разными
            вещами. Запрос эти поля тянул и раньше — их выбрасывал показ. */}
          {skills.length ? (
            <Section>
              <SectionTitle>{t('careerCenter.vacancies.facts.skills')}</SectionTitle>
              <TagRow>
                {skills.map((skill) => (
                  <Tag key={skill.id} $owned={owned.has(skill.id)}>
                    <SkillIcon
                      slug={skill.slug}
                      name={skill.name}
                      category={skill.category}
                      iconUrl={skill.iconUrl}
                      size={16}
                    />
                    {skill.name}
                  </Tag>
                ))}
              </TagRow>
            </Section>
          ) : null}

          {/* ТЗ 0073: то же соответствие, что у карточки ленты, — один объект
            с сервера, счётчики из его списков. */}
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.matchTitle')}</SectionTitle>
            <VacancySkillMatch match={match} skillHref={skillHref} />
          </Section>

          <Footer>
            {/* 0073: короткая подпись и знак перехода; полное имя — для читалки. */}
            <OpenLink
              href={vacancy.applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('careerCenter.vacancies.applyAria')}
              onClick={() => (head ? onApplyClick?.(head) : undefined)}>
              {t('careerCenter.vacancies.apply')}
              <CareerIcon name="externalLink" size={16} />
            </OpenLink>

            {canSave ? (
              <SecondaryAction type="button" disabled={actionLoading} onClick={onSave}>
                {t(isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save')}
              </SecondaryAction>
            ) : null}
          </Footer>

          <FooterNote>{t('careerCenter.vacancies.applyNote')}</FooterNote>
        </Body>
      </Drawer>
    );
  },
);

const CompanyLine = styled.div`
  margin-bottom: 20px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const FactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const FactRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
`;

const FactIcon = styled.span`
  display: inline-flex;
  color: ${Colors.gray_dark};
`;

const FactLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FactValue = styled.span`
  text-align: right;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const DescriptionText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span<{ $owned?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid ${({ $owned }) => ($owned ? Colors.signal_green : 'transparent')};
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

/** Переменные цвета: панель живёт в портале, вне [data-career-section]. */
const Body = styled.div`
  ${careerTokenVars}
`;

/**
 * Действия панели (ТЗ 0073).
 *
 * 🔴 Раньше подпись «Aplikuj na stronie pracodawcy (otworzy się w nowej
 * karcie)» с nowrap выходила за правый край панели. Теперь подпись короткая,
 * перенос разрешён, а на узкой панели (внутренняя ширина меньше 480 px, то
 * есть окно уже 528 px при ширине панели min(560px, 100vw) и полях 24 px)
 * кнопки встают друг под другом на всю ширину.
 */
const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 12px;
  margin-top: 28px;

  @media (max-width: 527px) {
    flex-direction: column;
  }
`;

const actionBase = `
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 10px 20px;
  border-radius: ${careerLayout.controlRadius}px;
  text-align: center;
  white-space: normal;
  cursor: pointer;

  @media (max-width: 527px) {
    width: 100%;
  }
`;

const SecondaryAction = styled.button`
  ${actionBase}
  border: 1px solid ${careerColor.line};
  background: transparent;
  color: ${careerColor.text};
  ${careerType.bodyStrong()};

  &:hover:not(:disabled) {
    background: ${careerColor.hover};
  }

  &:focus-visible {
    outline: 2px solid ${careerColor.text};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const OpenLink = styled.a`
  ${actionBase}
  border: 1px solid ${Colors.main};
  background-color: ${Colors.main};
  /* На жёлтой заливке только btn_text (правило в context/constants/colors.ts). */
  color: ${CTA_TEXT};
  ${careerType.bodyStrong()};

  &:hover {
    opacity: 0.9;
    color: ${CTA_TEXT};
  }

  &:focus-visible {
    outline: 2px solid ${careerColor.text};
    outline-offset: 2px;
  }
`;

/** Честная подпись: откликнуться из системы нельзя, отклик идёт у работодателя. */
const FooterNote = styled.p`
  margin: 12px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;
